import {
    ContentBlockFragment,
    ContentScoringConfigFragment,
    MainContentBlockQueryResult,
} from '@graphqlTypes/sitecore';
import { DeepReadonly } from '@src/utils/type-utils';
import { differenceInSeconds } from 'date-fns';
import { OrderContentBlock } from '../filter';

/**
 * sort all content blocks by score,This method mutates the content block arrays
 * @param contents
 * @param categoryScores
 * @param hashtagScores
 */
export function sortMainContentsByScores(
    contents: MainContentBlockQueryResult | null,
    categoryScores: Record<string, number>,
    hashtagScores: Record<string, number>,
    contentScoringConfig: DeepReadonly<ContentScoringConfigFragment>
) {
    contents?.Item?.sections.forEach((sec) => {
        const log = console.log;
        console.log = (message?: never, ...optionalParams: never[]) => {
            log(`===${sec.key}===\n${message}`, ...optionalParams);
        };
        sortContentBlocksByScores(
            sec.contentBlocks,
            categoryScores,
            hashtagScores,
            contentScoringConfig
        );
        console.log = log;
    });
    // re-order, selected articles have higher priority
    contents?.Item?.sections.forEach((sec) => {
        const selectedArticles = sec.displayArticles?.targetItems;
        sec.contentBlocks = OrderContentBlock(sec.contentBlocks, selectedArticles);
    });
}

/**
 * sort all content blocks by date,This method mutates the content block arrays
 * @param contents
 */
export function sortMainContentsByDate(contents: MainContentBlockQueryResult | null) {
    contents?.Item?.sections.forEach((sec) => {
        sortContentBlocksByDate(sec.contentBlocks);
    });
}

/**
 * for calendar events, sort all content blocks by event date,This method mutates the content block arrays
 * @param contents
 */
export function sortMainContentsByEventDate(contents: MainContentBlockQueryResult | null) {
    contents?.Item?.sections.forEach((sec) => {
        sortContentBlocksByEventDate(sec.contentBlocks);
    });
}

/**
 * Sorts content blocks by score. This method mutates the array and returns a reference to the same array.
 * @param contentBlocks
 * @param categoryScores
 * @param hashtagScores
 * @param contentScoringConfig
 */
export function sortContentBlocksByScores(
    contentBlocks: Array<ContentBlockFragment>,
    categoryScores: Record<string, number>,
    hashtagScores: Record<string, number>,
    contentScoringConfig: DeepReadonly<ContentScoringConfigFragment>
) {
    contentBlocks.sort((block1, block2) => {
        const score1 = getContentBlockScore(
            block1,
            categoryScores,
            hashtagScores,
            contentScoringConfig
        );
        const score2 = getContentBlockScore(
            block2,
            categoryScores,
            hashtagScores,
            contentScoringConfig
        );
        return score2 - score1;
    });
    return contentBlocks;
}

/**
 * Sorts content blocks by date. This method mutates the array and returns a reference to the same array.
 * @param contentBlocks
 */
export function sortContentBlocksByDate(contentBlocks: Array<ContentBlockFragment>) {
    contentBlocks.sort((block1, block2) => {
        const date1 = block1.startDatetime?.dateValue || Date.now();
        const date2 = block2.startDatetime?.dateValue || Date.now();
        return date1 - date2;
    });
    return contentBlocks;
}

/**
 * for calendar events, Sorts content blocks by event date. This method mutates the array and returns a reference to the same array.
 * @param contentBlocks
 */
export function sortContentBlocksByEventDate(contentBlocks: Array<Partial<ContentBlockFragment>>) {
    contentBlocks.sort((block1, block2) => {
        const date1 = block1.eventDate?.dateValue || Date.now();
        const date2 = block2.eventDate?.dateValue || Date.now();
        return date1 - date2;
    });
    return contentBlocks;
}

export function getContentBlockScore(
    block: DeepReadonly<ContentBlockFragment>,
    categoryScores: Record<string, number>,
    hashtagScores: Record<string, number>,
    contentScoringConfig: DeepReadonly<ContentScoringConfigFragment>
): number {
    // category
    let categoryScore = 0;
    block.category?.categories?.forEach((cat) => {
        const score = categoryScores[cat.name] || 0;
        categoryScore += score;
    });

    // hashtag
    let hashtagScore = 0;
    block.hashTags?.tags?.forEach((tag) => {
        const score = hashtagScores[tag.name] || 0;
        hashtagScore += score;
    });

    //days passed
    let dateScore = 0;
    const startDate = block.startDatetime?.dateValue || 0;
    const secondsPassed = differenceInSeconds(Date.now(), startDate);
    const secondsIn1st = (contentScoringConfig.pDateRange1st?.intValue || 0) * 86400;
    const secondsIn2nd = (contentScoringConfig.pDateRange2nd?.intValue || 0) * 86400;
    const secondsIn3rd = (contentScoringConfig.pDateRange3rd?.intValue || 0) * 86400;
    if (secondsPassed >= 0) {
        if (secondsPassed <= secondsIn1st) {
            dateScore = contentScoringConfig.pDatePoints1st?.numberValue || 0;
        } else if (secondsPassed <= secondsIn2nd) {
            dateScore = contentScoringConfig.pDatePoints2nd?.numberValue || 0;
        } else if (secondsPassed <= secondsIn3rd) {
            dateScore = contentScoringConfig.pDatePoints3rd?.numberValue || 0;
        }
    }

    //editors pick
    let editorsPickScore = 0;
    if (block.editorsPick?.boolValue) {
        editorsPickScore = contentScoringConfig.editorsPick?.numberValue || 0;
    }
    const totalScore = categoryScore + hashtagScore + dateScore + editorsPickScore;

    // 總分統計信息打印，生產要打開
    const getLogSwitch = JSON.parse(process.env.NEXT_PUBLIC_TOTAL_SCORE_LOG ?? 'true');
    getLogSwitch &&
        console.log(
            `${block.key}-----${totalScore}  category:${categoryScore} hashtag:${hashtagScore} date:${dateScore} editorsPick:${editorsPickScore}`
        );
    return totalScore;
}
